import { ErrorMessage, Field, Label } from '@/components/base/fieldset'
import { Input, InputGroup } from '@/components/base/input'
import { Textarea } from '@/components/base/textarea'
import { format, formatISO } from 'date-fns'
import { Selector as AssetTypeMaintenanceScheduleSelector } from '@/components/asset_type_maintenance_schedule/selector'
import { Selector as ClientSelector } from '@/components/client/selector'
import { Selector as RoomTypeSelector } from '@/components/accommodation_room_type/selector'
import { Selector as ManufacturerSelector } from '@/components/asset_type/manufacturer/selector'
import { Selector as SpecificationSelector } from '@/components/specification/selector'
import { Selector as AssetTypeSelector } from '@/components/asset_type/selector'
import { Selector as ProductMultiplierTypeSelector } from '@/components/product_multiplier_type/selector'
import { Selector as AssetTypeModelSelector } from '@/components/asset_type/model/selector'
import { Selector as VenueSelector } from '@/components/venue/selector'
import { Selector as WarehouseSelector } from '@/components/warehouse/selector'
import { Selector as UserSelector } from '@/components/user/selector'
import { Selector as ShiftRoleSelector } from '@/components/shift_role/selector'
import { Selector as CategorySelector } from '@/components/category/selector'
import { Selector as ClientTypeSelector } from '@/components/client_type/selector'
import { Selector as CustomFieldSelector } from '@/components/custom_field/selector'
import { Selector as AssetSelector } from '@/components/asset/selector'
import { Selector as ResultSelector } from '@/components/maintenance_event/selector'
import { Selector as MaintanenceSelector } from '@/components/maintenance_type/selector'
import { Selector as MaintanenceTaskTypeSelector } from '@/components/maintenance_type_task/selector'
import { Selector as MultiplierTypeSelector } from '@/components/multiplier_type/multiplier_type/selector'
import { Selector as MystroTestSelector } from '@/components/mystro_test/selector'
import { TSearchable } from '@/components/base/combobox'
import { Listbox, ListboxLabel, ListboxOption } from '@/components/base/listbox'
import { Button } from '@/components/base/button'
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/solid'
import { Text } from '@/components/base/text'
import { Switch } from '@/components/base/switch'
import { Color } from '@/types'
import clsx from 'clsx'
import { CurrencyInput } from './form/currency_input'
import { WeightInput } from './form/weight_input'
import { ReactNode } from 'react'
import { DimensionInput } from './form/dimension_input'

interface Props<T> {
  attributes: T
  setAttributes: (attributes: T) => void
  mutationError?: string
  fieldErrors?: { [field: string]: string }
}

type FieldType = 'text' | 'number' | 'email' | 'textarea' | 'switch' | 'currency' | 'client' | 'client-type' | 'venue' |
                 'warehouse' | 'user' | 'custom-field' | 'category' | 'shift-role' | 'datetime' | 'select' |
                 'percentage' | 'color' | 'manufacturer' | 'asset-type-model' | 'weight' | 'specification' |
                 'asset-type' | 'product-multiplier-type' | 'asset-type-maintenance-schedule' | 'integer' | 'asset' |
                 'maintenance-type' | 'positive-integer' | 'multiplier-type' | 'maintenance-task-type' | 'mystro-test' |
                 'maintenance-event-result' | 'room-type' | 'non-negative-integer' | 'non-negative-number' | 'dimension'

export const builder = <T,>({ attributes, setAttributes, mutationError, fieldErrors }: Props<T>) => {
  const fieldFor = (
    type: FieldType,
    field: keyof T,
    options: { value: string, display?: ReactNode | string }[],
    disabled: boolean,
    filters: object
  ) => {
    const fieldError = fieldErrors?.[field as string]

    // this keeps the component controlled, even if we haven't finished init yet
    const value = attributes[field] as string || ''

    const common = { invalid: !!fieldError, value, disabled }

    const onChangeText = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => setAttributes({ ...attributes, [field]: e.target.value })
    const onChangeNumber = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => setAttributes({ ...attributes, [field]: parseFloat(e.target.value) })
    const onChangeInteger = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => setAttributes({ ...attributes, [field]: parseInt(e.target.value || '0') })
    const onChangeDatetime = (
      e: React.ChangeEvent<HTMLInputElement>
    ) => { setAttributes({ ...attributes, [field]: formatISO(e.target.value) }) }
    const onChangeSelector = (
      value: TSearchable | null
    ) => { setAttributes({ ...attributes, [field]: value?.id || null }) }
    const onChangeSelect = (
      value: string
    ) => { setAttributes({ ...attributes, [field]: value }) }
    const onChangeSwitch = (
      value: boolean
    ) => { setAttributes({ ...attributes, [field]: value }) }
    const onChangeCurrency = (
      value: number
    ) => { setAttributes({ ...attributes, [field]: value }) }
    const onChangePercentage = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => { setAttributes({ ...attributes, [field]: parseInt(e.target.value) }) }
    const onChangeColor = (
      value: Color
    ) => { setAttributes({ ...attributes, [field]: value }) }

    switch (type) {
      case 'text': return <Input {...common} type="text" onChange={onChangeText} />
      case 'number': return <Input {...common} step={0.01} type="number" onChange={onChangeNumber} />
      case 'non-negative-number': return <Input {...common} min={0} step={0.01} type="number" onChange={onChangeNumber} />
      case 'integer': return <Input {...common} step={1} type="number" value={attributes[field] as number || 0} onChange={onChangeInteger} />
      case 'positive-integer': return <Input {...common} min={1} step={1} type="number" value={attributes[field] as number || 0} onChange={onChangeInteger} />
      case 'non-negative-integer': return <Input {...common} min={0} step={1} type="number" value={attributes[field] as number || 0} onChange={onChangeInteger} />
      case 'email': return <Input {...common} type="email" onChange={onChangeText} />
      case 'textarea': return <Textarea {...common} rows={6} onChange={onChangeText} />
      case 'switch': return <Switch checked={attributes[field] as boolean || false} className="ml-4 self-center" onChange={onChangeSwitch} />
      case 'currency': return <CurrencyInput {...common} value={attributes[field] as number || 0} onChange={onChangeCurrency} />
      case 'weight': return <WeightInput {...common} value={attributes[field] as number || 0} onChange={onChangeCurrency} />
      case 'dimension': return <DimensionInput {...common} value={attributes[field] as number || 0} onChange={onChangeCurrency} />
      case 'datetime': return <Input {...common} type="datetime-local" value={attributes[field] ? format(attributes[field] as string, 'yyyy-MM-dd\'T\'HH:mm') : ''} onChange={onChangeDatetime} onFocus={e => e.target.showPicker()} />
      case 'asset-type': return <AssetTypeSelector {...common} onChange={onChangeSelector} />
      case 'asset-type-maintenance-schedule': return <AssetTypeMaintenanceScheduleSelector {...common} onChange={onChangeSelector} />
      case 'client': return <ClientSelector {...common} onChange={onChangeSelector} />
      case 'client-type': return <ClientTypeSelector {...common} onChange={onChangeSelector} />
      case 'manufacturer': return <ManufacturerSelector {...common} onChange={onChangeSelector} />
      case 'maintenance-type': return <MaintanenceSelector {...common} onChange={onChangeSelector} />
      case 'maintenance-task-type': return <MaintanenceTaskTypeSelector {...common} onChange={onChangeSelector} />
      case 'maintenance-event-result': return <ResultSelector {...common} onChange={onChangeSelector} />
      case 'multiplier-type': return <MultiplierTypeSelector {...common} onChange={onChangeSelector} />
      case 'product-multiplier-type': return <ProductMultiplierTypeSelector {...common} onChange={onChangeSelector} />
      case 'specification': return <SpecificationSelector {...common} onChange={onChangeSelector} />
      case 'asset-type-model': return <AssetTypeModelSelector {...common} onChange={onChangeSelector} />
      case 'asset': return <AssetSelector {...common} onChange={onChangeSelector} />
      case 'venue': return <VenueSelector {...common} onChange={onChangeSelector} />
      case 'warehouse': return <WarehouseSelector {...common} onChange={onChangeSelector} />
      case 'user': return <UserSelector {...common} onChange={onChangeSelector} />
      case 'category': return <CategorySelector {...common} onChange={onChangeSelector} />
      case 'custom-field': return <CustomFieldSelector {...common} filters={filters} onChange={onChangeSelector} />
      case 'room-type': return <RoomTypeSelector {...common} onChange={onChangeSelector} />
      case 'shift-role': return <ShiftRoleSelector {...common} onChange={onChangeSelector} />
      case 'mystro-test': return <MystroTestSelector {...common} onChange={onChangeSelector} />
      case 'select': return (
        <Listbox {...common} onChange={onChangeSelect}>
          { options?.map(option => (
            <ListboxOption key={option.value} value={option.value}>
              <ListboxLabel>{ option.display || option.value }</ListboxLabel>
            </ListboxOption>
          )) }
        </Listbox>
      )
      case 'percentage': return (<>
        <div className="flex flex-row gap-2" data-slot="control">
          <InputGroup className="grow">
            <Input {...common} step={1} type="number" onChange={onChangePercentage} />
            <span className="text-sm/4" data-slot="icon">%</span>
          </InputGroup>
          <Button onClick={() => setAttributes({ ...attributes, discount: 10 })}>10%</Button>
          <Button onClick={() => setAttributes({ ...attributes, discount: 100 })}>100%</Button>
        </div>
      </>)
      case 'color': {
        const colors: Record<Color, { bg: string, border: string }> = {
          red: { bg: 'bg-red-500', border: 'border-red-400' },
          orange: { bg: 'bg-orange-500', border: 'border-orange-400' },
          yellow: { bg: 'bg-yellow-500', border: 'border-yellow-400' },
          lime: { bg: 'bg-lime-500', border: 'border-lime-400' },
          green: { bg: 'bg-green-500', border: 'border-green-400' },
          emerald: { bg: 'bg-emerald-500', border: 'border-emerald-400' },
          teal: { bg: 'bg-teal-500', border: 'border-teal-400' },
          sky: { bg: 'bg-sky-500', border: 'border-sky-400' },
          blue: { bg: 'bg-blue-500', border: 'border-blue-400' },
          indigo: { bg: 'bg-indigo-500', border: 'border-indigo-400' },
          purple: { bg: 'bg-purple-500', border: 'border-purple-400' },
          pink: { bg: 'bg-pink-500', border: 'border-pink-400' },
          gray: { bg: 'bg-gray-500', border: 'border-gray-400' },
          black: { bg: 'bg-black', border: 'border-gray-500' },
        }
        return (<>
          <div className="flex flex-row gap-2">
            { Object.entries(colors).map(([color, classes]) => (
              <button
                key={color}
                type="button"
                className={clsx(
                  'size-12 border rounded-lg',
                  classes.bg,
                  value == color ? 'border-white border-2' : classes.border,
                )}
                onClick={() => onChangeColor(color as Color)}
              />
            )) }
          </div>
        </>)
      }
    }
  }

  const buildField = ({
    field, type, options = [], disabled = false, filters = {}, ...props
  }: {
    name: string
    field: keyof T
    type: FieldType
    options?: { value: string, display?: ReactNode | string }[]
    disabled?: boolean
    filters?: object
    hideLabel?: boolean
  }) => {
    return buildCustomField({ ...props, field, input: fieldFor(type, field, options, disabled, filters) })
  }

  const buildCustomField = ({
    name, field, input, disabled = false, hideLabel = false
  }: {
    name: string
    field: keyof T
    input: ReactNode
    disabled?: boolean
    hideLabel?: boolean
  }) => {
    const fieldError = fieldErrors?.[field as string]

    return (
      <Field disabled={disabled}>
        { !hideLabel && <Label>{ name }</Label> }
        { input }
        { fieldError && <ErrorMessage>{ fieldError }</ErrorMessage> }
      </Field>
    )
  }

  const buildQueryError = () => {
    if (!mutationError) return <></>

    return (
      <Field className="flex flex-row gap-2 items-center">
        <Text>An error prevented saving:</Text> <ErrorMessage>{ mutationError }</ErrorMessage>
      </Field>
    )
  }

  const buildBaseError = () => {
    const fieldError = fieldErrors?.base
    if (!fieldError) return <></>

    return (
      <Field className="flex flex-row gap-2 items-center">
        <Text>An error prevented saving:</Text> <ErrorMessage>{ fieldError }</ErrorMessage>
      </Field>
    )
  }

  const saveButton = (
    save: () => void,
    loading: boolean,
    saveSuccess?: boolean,
    prompt?: string,
    color?: 'red' | 'green'
  ) => (
    <div className="flex flex-row items-center justify-end gap-3">
      <div className="relative w-8 h-8">
        <CheckIcon
          className={clsx(
            'absolute inset-0 transition duration-150 text-green-600 h-8 w-8',
            saveSuccess == true ? 'opacity-100' : 'opacity-0'
          )}
        />
        <XMarkIcon
          className={clsx(
            'absolute inset-0 transition duration-150 text-red-600 h-8 w-8',
            saveSuccess == false ? 'opacity-100' : 'opacity-0'
          )}
        />
      </div>
      <Button color={color || 'green'} disabled={loading} onClick={save}>
        { loading ? 'Saving...' : prompt || 'Save' }
      </Button>
    </div>
  )

  return { buildField, buildCustomField, buildQueryError, buildBaseError, saveButton }
}
